import Modal from '@uikit/Modal/Modal';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from '@hooks/useDirtyForm';
import SubmitButton from '@uikit/FormItems/SubmitButton/SubmitButton';
import { ControlledInput } from '@uikit/FormItems/ControlledItems';
import { useDebounce } from '@hooks/useDebounce';
import { useLazySearchForMemberQuery } from '@store/rtk/members';
import { getLocationAsText, handleImgError } from '@src/utils';
import { FALLBACK_IMAGE, IMAGE_STORAGE_URL } from '@utils/constants';
import { setIsMemberSaved } from '@store/slices/members/info/membersInfoSlice';
import { updateXHeaderId } from '@utils/storage';
import { useDispatch } from 'react-redux';
import { useOutsideClick } from '@hooks/useOutsideClick';
import TruncatedText from '@component/TruncatedText/TruncatedText';
import { CakeIcon, RideLocationIcon, UserIdIcon } from '@src/svg';
import { PlusIcon } from '@svg/PlusIcon';
import { BaseButton } from '@uikit/Button/BaseButton';
import { logger } from '@utils/logger';
import { toastError } from '@utils/helpers';
import { toastMessage } from '@utils/toastMessage';

interface memberSelectModalProps {
  title: string;
  handleCreate: () => void;
  closeModal: () => void;
  onAddNewMember: () => void;
  setNavigateId?: (id: number | null) => void;
}

const MemberSelectModal: FC<memberSelectModalProps> = ({
  title,
  handleCreate,
  closeModal,
  onAddNewMember,
  setNavigateId,
}) => {
  const dispatch = useDispatch();
  const [getMembers, { data, isFetching }] = useLazySearchForMemberQuery();
  const searchRef = useRef<any>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<boolean>(false);

  const {
    control,
    setValue,
    handleSubmit,
    setFocus,
    formState: { dirtyFields, isSubmitting },
  } = useForm({
    defaultValues: {
      search: '',
    },
  });

  useEffect(() => {
    const timer = setTimeout(() => setFocus('search'), 100);
    return () => clearTimeout(timer);
  }, [setFocus]);

  useOutsideClick({
    ref: searchRef,
    onOutsideClick: () => setOpen(false),
  });

  const searchedValue = useCallback(async () => {
    if (!searchValue) return;

    try {
      await getMembers({ query: searchValue }).unwrap();
    } catch (err) {
      toastError(err, toastMessage.MEMBER.failedToSearchMembers);
      logger.error(`Error fetching member:`, err);
    }
  }, [searchValue]);

  useEffect(() => {
    searchedValue();
  }, [searchValue]);

  useEffect(() => {
    setOpen(!isFetching);
  }, [isFetching]);

  const handleOnchange = useCallback((data) => {
    setSearchValue(data.search);
  }, []);

  const debouncedSearch = useDebounce(handleOnchange, 1000);

  return (
    <>
      <Modal
        title="Select Member"
        closeClick={closeModal}
        maxWidth="420px"
        dataTestId="msel"
      >
        <div className="member_select-modal" data-test-id="msel">
          <div className="member_select__grid" data-test-id="msel-grid">
            <ControlledInput
              dataTestId="member-create-search-input"
              control={control}
              name="search"
              title="Enter Member ID, Member Name, Date of Birth, or Address"
              required
              placeholder="Member Name"
              onChange={handleSubmit(debouncedSearch)}
              onFocus={() => setOpen(true)}
            />
            {searchValue && open && (
              <div
                className="member_select__dropdown"
                ref={searchRef}
                data-test-id="msel-dd"
              >
                {data && data.length > 0 ? (
                  data.map(
                    ({
                      id,
                      image,
                      fullName,
                      pronouns,
                      memberId,
                      dob,
                      address,
                    }) => (
                      <div
                        key={id}
                        id={`${id}`}
                        className="select_option"
                        data-test-id={`create-select-modal-option-${id}`}
                        onClick={() => {
                          updateXHeaderId('member', id);
                          setNavigateId?.(id);
                          setOpen(false);
                          setSelectedMember(true);
                          setValue('search', fullName);
                          dispatch(setIsMemberSaved(true));
                        }}
                      >
                        <img
                          src={
                            image
                              ? `${IMAGE_STORAGE_URL}/${image}`
                              : FALLBACK_IMAGE
                          }
                          alt="member select image"
                          className="uploading-image"
                          onError={handleImgError}
                        />
                        <div
                          className="contact_info"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          <div className="contact_name">
                            {/*<span>{el.firstName} </span>*/}
                            {/*<span>{el.lastName} </span>*/}
                            <span>{fullName} </span>
                            {pronouns && <span>({pronouns})</span>}
                          </div>
                          <div
                            className="contact_jobTitle"
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              color: '#FFFFFFB3',
                            }}
                          >
                            <span
                              style={{
                                alignSelf: 'center',
                                display: 'flex',
                                gap: '8px',
                              }}
                            >
                              <UserIdIcon
                                color="#FFFFFFB3"
                                width={16}
                                height={16}
                              />
                              {memberId}
                            </span>
                            <span
                              style={{
                                alignSelf: 'center',
                                display: 'flex',
                                gap: '8px',
                              }}
                            >
                              <CakeIcon
                                color="#FFFFFFB3"
                                width={16}
                                height={16}
                              />
                              {dob}
                            </span>
                          </div>
                          <div
                            className="contact_jobTitle"
                            style={{
                              color: '#FFFFFFB3',
                              gap: '8px',
                            }}
                          >
                            <TruncatedText
                              Icon={
                                <RideLocationIcon
                                  color="#FFFFFFB3"
                                  width={16}
                                  height={16}
                                />
                              }
                              text={getLocationAsText(address)}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  )
                ) : (
                  <div
                    id={`notFound`}
                    className="select_option"
                    data-test-id="msel-nf"
                  >
                    <div className="contact_info">
                      <div className="contact_name">No member found</div>
                      {/*<div className="contact_jobTitle">{el.label}</div>*/}
                    </div>
                  </div>
                )}
                <div className="select_option" data-test-id="msel-add-new">
                  <BaseButton
                    dataTestId="msel-add-new"
                    title="Add New Member"
                    Icon={<PlusIcon width={16} height={16} />}
                    onClick={onAddNewMember}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="member_select__bottom" data-test-id="msel-ft">
            <SubmitButton
              dataTestId="select-member-modal-create"
              title={title}
              onClick={handleSubmit(handleCreate)}
              dirtyFields={dirtyFields}
              disabled={isSubmitting || !selectedMember}
              loading={isSubmitting}
              disableWhenUnchangedFields
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default MemberSelectModal;
